// Filter chips above the Criteria Checklist.

import type { CriteriaItemStatus, CriteriaTier } from "@/api/dealFlow/types";

export type TierFilter = CriteriaTier | "All";
export type StatusFilter = CriteriaItemStatus | "All";

const TIER_FILTER_LABEL: Record<TierFilter, string> = {
  All: "All Tiers",
  DealKiller: "Deal Killers",
  RedFlag: "Red Flags",
  NeedsImprovement: "Needs Improvement",
};

const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  All: "All Statuses",
  Pending: "Pending",
  Escalated: "Escalated",
  Approved: "Approved",
  Declined: "Declined",
  Waived: "Waived",
};

interface CriteriaChecklistFiltersProps {
  tier: TierFilter;
  status: StatusFilter;
  onTierChange: (next: TierFilter) => void;
  onStatusChange: (next: StatusFilter) => void;
}

export function CriteriaChecklistFilters({
  tier,
  status,
  onTierChange,
  onStatusChange,
}: CriteriaChecklistFiltersProps) {
  return (
    <div className="flex flex-wrap gap-space-3">
      <ChipGroup
        label="Tier"
        value={tier}
        options={["All", "DealKiller", "RedFlag", "NeedsImprovement"] as TierFilter[]}
        labels={TIER_FILTER_LABEL}
        onChange={(v) => onTierChange(v as TierFilter)}
      />
      <ChipGroup
        label="Status"
        value={status}
        options={
          [
            "All",
            "Pending",
            "Escalated",
            "Approved",
            "Declined",
            "Waived",
          ] as StatusFilter[]
        }
        labels={STATUS_FILTER_LABEL}
        onChange={(v) => onStatusChange(v as StatusFilter)}
      />
    </div>
  );
}

interface ChipGroupProps<T extends string> {
  label: string;
  value: T;
  options: ReadonlyArray<T>;
  labels: Record<T, string>;
  onChange: (next: T) => void;
}

function ChipGroup<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
}: ChipGroupProps<T>) {
  return (
    <div className="flex items-center gap-space-2">
      <span className="font-display text-label uppercase tracking-label text-gold-shadow">
        {label}:
      </span>
      <div className="flex flex-wrap gap-space-1">
        {options.map((opt) => {
          const isOn = opt === value;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-space-2 py-[3px] text-caption rounded-pill border ${
                isOn
                  ? "bg-onyx border-onyx text-white-pure"
                  : "bg-white-pure border-border-light text-gray-mid hover:text-onyx"
              }`}
            >
              {labels[opt]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
